'use client';
import React, { useEffect } from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const useCases = [
  {
    tag: '[01]',
    title: 'Pre-production vulnerability elimination',
    description:
      'SAST and SCA gates run on every pull request. Developers get inline findings with remediation guidance before code ever reaches staging, not after a quarterly pen test.',
  },
  {
    tag: '[02]',
    title: 'Runtime attack surface reduction',
    description:
      'DAST probes running applications for injection, authentication bypass, and business-logic flaws that static analysis cannot detect. Findings are triaged by exploitability, not just CVSS score.',
  },
  {
    tag: '[03]',
    title: 'Supply chain and dependency risk',
    description:
      'SCA across your full dependency tree, direct and transitive. CVEs enriched with reachability analysis so your team knows which vulnerabilities are actually exploitable in your context.',
  },
  {
    tag: '[04]',
    title: 'Business-logic vulnerability identification',
    description:
      'Automated scanners miss logic flaws. Our engineers review authentication flows, authorisation models, and data handling patterns to find vulnerabilities that tools simply cannot detect.',
  },
];

const outcomes = [
  { metric: '< 24h', label: 'Mean time to triage for critical CVEs' },
  { metric: '90%+', label: 'Reduction in false-positive noise from scanners' },
  { metric: 'Day 1', label: 'Pipeline integration, no sprint disruption' },
  { metric: '100%', label: 'Findings delivered with remediation guidance' },
];

const techStack = [
  { category: 'SAST', tools: ['Semgrep', 'CodeQL', 'Checkmarx', 'Snyk Code'] },
  { category: 'DAST', tools: ['OWASP ZAP', 'Burp Suite Enterprise', 'Nuclei'] },
  { category: 'SCA', tools: ['Snyk Open Source', 'Dependabot', 'OWASP Dependency-Check'] },
  { category: 'CVE Intelligence', tools: ['NVD enrichment', 'EPSS scoring', 'CISA KEV correlation'] },
  { category: 'Pipeline', tools: ['GitHub Actions', 'GitLab CI', 'Jenkins', 'Azure DevOps'] },
  { category: 'Reporting', tools: ['SBOM generation (CycloneDX, SPDX)', 'Custom dashboards'] },
];

const differentiators = [
  {
    title: 'Signal, not noise',
    body: 'Raw scanner output is useless without context. Every finding is triaged by a senior engineer, enriched with business impact, exploitability, and a concrete remediation path.',
  },
  {
    title: 'Embedded, not external',
    body: 'We work inside your engineering team. Security reviews happen in your PRs, your standups, your sprint cycles, not in a quarterly report that arrives too late to act on.',
  },
  {
    title: 'Continuous, not point-in-time',
    body: 'AppSec is not an annual pen test. Pipelines run on every commit. CVE feeds are monitored daily. Your attack surface is tracked continuously, not sampled once a year.',
  },
];

export default function AppSecPage() {
  useScrollAnimation();

  return (
    <div className="min-h-screen bg-[#050a0f] text-[#f5f5f8] noise-overlay-animated">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="orb orb-blue" style={{ width: '600px', height: '600px', top: '-150px', left: '-100px', opacity: 0.35 }} />
          <div className="orb orb-teal" style={{ width: '400px', height: '400px', bottom: '0', right: '5%', opacity: 0.2 }} />
        </div>
        <div className="relative z-10 max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 fade-in-up">
            <Link href="/" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Home</Link>
            <span className="text-[#333]">/</span>
            <Link href="/services" className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest hover:text-[#80edfa] transition-colors">Services</Link>
            <span className="text-[#333]">/</span>
            <span className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest">AppSec</span>
          </div>

          <div className="max-w-[900px]">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4 fade-in-up" style={{ transitionDelay: '0.05s' }}>
              [AppSec & Vulnerability Management]
            </p>
            <h1 className="font-heading text-[clamp(2.5rem,6vw,4.5rem)] text-[#f5f5f8] font-medium leading-[1.05] mb-6 fade-in-up" style={{ transitionDelay: '0.1s' }}>
              Find what matters.<br />
              <span className="text-[#80edfa]">Fix it before it ships.</span>
            </h1>
            <p className="text-[#aaa] text-lg leading-relaxed max-w-[680px] mb-10 fade-in-up" style={{ transitionDelay: '0.15s' }}>
              Automated scanners produce noise. Our engineers produce signal. We integrate SAST, DAST, and SCA directly into your CI/CD pipelines, then do the work tools cannot: triaging every CVE with business context so your teams know exactly what to fix, in what order, and why.
            </p>
            <div className="flex flex-wrap gap-4 fade-in-up" style={{ transitionDelay: '0.2s' }}>
              <a href="#contact" className="btn-primary">Talk to an AppSec engineer</a>
              <a href="#use-cases" className="btn-secondary">See use cases</a>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="border-t border-[#222] py-16 bg-[#050a0f]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#222]">
            {outcomes?.map((o, i) => (
              <div key={i} className="bg-[#050a0f] px-8 py-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-heading text-[clamp(2rem,4vw,3rem)] text-[#80edfa] font-medium mb-2">{o?.metric}</p>
                <p className="font-mono-brand text-[0.65rem] text-[#555] uppercase tracking-widest leading-relaxed">{o?.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-24 mb-16">
            <div className="lg:w-1/3 fade-in-left">
              <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Use cases</p>
              <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight">
                Where AppSec makes the real difference
              </h2>
            </div>
            <div className="lg:w-1/2 flex items-end fade-in-right" style={{ transitionDelay: '0.1s' }}>
              <p className="text-[#aaa] text-base leading-relaxed">
                From pre-production gates to runtime monitoring, TechSonar AppSec engineers cover the full vulnerability lifecycle, not just the scan.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-[#222]">
            {useCases?.map((uc, i) => (
              <div key={i} className="bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.08}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{uc?.tag}</p>
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{uc?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{uc?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Depth */}
      <section className="border-t border-[#222] py-20 lg:py-28 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Technical depth</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              The tooling stack behind the programme
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {techStack?.map((t, i) => (
              <div key={i} className="border border-[#222] bg-[#181b1f] p-6 fade-in-up" style={{ transitionDelay: `${i * 0.07}s` }}>
                <p className="font-mono-brand text-[0.65rem] text-[#80edfa] uppercase tracking-widest mb-4">{t?.category}</p>
                <div className="flex flex-wrap gap-2">
                  {t?.tools?.map((tool, j) => (
                    <span key={j} className="text-xs text-[#aaa] border border-[#333] px-3 py-1 font-mono-brand">{tool}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className="border-t border-[#222] py-20 lg:py-28">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <div className="mb-14 fade-in-up">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-4">Why TechSonar</p>
            <h2 className="font-heading text-[clamp(1.75rem,3.5vw,2.75rem)] text-[#f5f5f8] font-medium leading-tight max-w-[600px]">
              AppSec that actually reduces risk
            </h2>
          </div>

          <div className="flex flex-col lg:flex-row gap-px bg-[#222]">
            {differentiators?.map((d, i) => (
              <div key={i} className="flex-1 bg-[#050a0f] p-8 lg:p-10 fade-in-up" style={{ transitionDelay: `${i * 0.1}s` }}>
                <div className="w-8 h-px bg-[#80edfa] mb-6" />
                <h3 className="font-heading text-xl text-[#f5f5f8] font-medium mb-3">{d?.title}</h3>
                <p className="text-sm text-[#aaa] leading-relaxed">{d?.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-[#222] py-20 bg-[#0a0f14]">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div className="fade-in-left">
            <p className="font-mono-brand text-[0.7rem] text-[#80edfa] uppercase tracking-widest mb-3">Get started</p>
            <h2 className="font-heading text-[clamp(1.5rem,3vw,2.25rem)] text-[#f5f5f8] font-medium">
              Ready to build AppSec into your pipeline?
            </h2>
          </div>
          <div className="flex flex-wrap gap-4 fade-in-right" style={{ transitionDelay: '0.1s' }}>
            <a href="#contact" className="btn-primary">Book a scoping call</a>
            <Link href="/services" className="btn-secondary">View all services</Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
